import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Container,
	Paper,
	Typography,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Snackbar,
	Alert,
	CircularProgress,
} from "@mui/material";
import { Download, Upload, Edit2, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../config/supabase";
import { ROLES } from "../../config/supabase";

const STORAGE_BUCKET = "downloads";
const TABLE_NAME = "download_documents";

const Downloads = () => {
	const { user } = useAuth();
	const [documents, setDocuments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedDoc, setSelectedDoc] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [uploading, setUploading] = useState(false);

	const [form, setForm] = useState({
		name: "",
		description: "",
		file: null,
	});

	const isAdmin = user?.profile?.role === ROLES.FUNMIYO_ADMIN;

	const fetchDocuments = async () => {
		try {
			const { data, error } = await supabase
				.from(TABLE_NAME)
				.select("*")
				.order("created_at", { ascending: false });

			if (error) throw error;
			setDocuments(data || []);
		} catch (err) {
			console.error("Error fetching documents:", err);
			setSnackbar({ open: true, message: "Failed to load documents", severity: "error" });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchDocuments();
	}, []);

	const resetForm = () => {
		setForm({ name: "", description: "", file: null });
		setSelectedDoc(null);
	};

	const handleUpload = async () => {
		if (!form.name.trim()) {
			setSnackbar({ open: true, message: "Name is required", severity: "warning" });
			return;
		}
		if (!form.file) {
			setSnackbar({ open: true, message: "Please select a file", severity: "warning" });
			return;
		}
		setUploading(true);
		try {
			const fileExt = form.file.name.split(".").pop();
			const fileName = `${Date.now()}-${form.file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;
			const filePath = fileName;

			const { error: uploadError } = await supabase.storage
				.from(STORAGE_BUCKET)
				.upload(filePath, form.file, {
					contentType: form.file.type,
					upsert: false,
				});

			if (uploadError) throw uploadError;

			const { error: insertError } = await supabase.from(TABLE_NAME).insert({
				name: form.name.trim(),
				description: (form.description || "").trim() || null,
				file_path: filePath,
				file_name: form.file.name,
			});

			if (insertError) {
				await supabase.storage.from(STORAGE_BUCKET).remove([filePath]);
				throw insertError;
			}

			setSnackbar({ open: true, message: "Document uploaded successfully", severity: "success" });
			setUploadDialogOpen(false);
			resetForm();
			fetchDocuments();
		} catch (err) {
			console.error("Error uploading document:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to upload document",
				severity: "error",
			});
		} finally {
			setUploading(false);
		}
	};

	const handleEdit = async () => {
		if (!selectedDoc) return;
		if (!form.name.trim()) {
			setSnackbar({ open: true, message: "Name is required", severity: "warning" });
			return;
		}
		setUploading(true);
		try {
			const updates = {
				name: form.name.trim(),
				description: (form.description || "").trim() || null,
				updated_at: new Date().toISOString(),
			};
			if (form.file) {
				const filePath = `${Date.now()}-${form.file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;
				const { error: uploadError } = await supabase.storage
					.from(STORAGE_BUCKET)
					.upload(filePath, form.file, {
						contentType: form.file.type,
						upsert: false,
					});
				if (uploadError) throw uploadError;
				await supabase.storage.from(STORAGE_BUCKET).remove([selectedDoc.file_path]);
				updates.file_path = filePath;
				updates.file_name = form.file.name;
			}
			const { error } = await supabase.from(TABLE_NAME).update(updates).eq("id", selectedDoc.id);
			if (error) throw error;
			setSnackbar({ open: true, message: "Document updated successfully", severity: "success" });
			setEditDialogOpen(false);
			resetForm();
			fetchDocuments();
		} catch (err) {
			console.error("Error updating document:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to update document",
				severity: "error",
			});
		} finally {
			setUploading(false);
		}
	};

	const handleDelete = async () => {
		if (!selectedDoc) return;
		setUploading(true);
		try {
			await supabase.storage.from(STORAGE_BUCKET).remove([selectedDoc.file_path]);
			const { error } = await supabase.from(TABLE_NAME).delete().eq("id", selectedDoc.id);
			if (error) throw error;
			setSnackbar({ open: true, message: "Document deleted successfully", severity: "success" });
			setDeleteDialogOpen(false);
			setSelectedDoc(null);
			fetchDocuments();
		} catch (err) {
			console.error("Error deleting document:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to delete document",
				severity: "error",
			});
		} finally {
			setUploading(false);
		}
	};

	const handleDownload = async (doc) => {
		try {
			const { data, error } = await supabase.storage
				.from(STORAGE_BUCKET)
				.createSignedUrl(doc.file_path, 60);

			if (error) throw error;
			if (data?.signedUrl) {
				window.open(data.signedUrl, "_blank", "noopener,noreferrer");
			}
		} catch (err) {
			console.error("Error getting download URL:", err);
			setSnackbar({
				open: true,
				message: "Failed to get download link",
				severity: "error",
			});
		}
	};

	const openEditDialog = (doc) => {
		setSelectedDoc(doc);
		setForm({
			name: doc.name,
			description: doc.description || "",
			file: null,
		});
		setEditDialogOpen(true);
	};

	const openDeleteDialog = (doc) => {
		setSelectedDoc(doc);
		setDeleteDialogOpen(true);
	};

	return (
		<Container maxWidth="lg" sx={{ py: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
				<Typography variant="h5" fontWeight={600}>
					Downloads
				</Typography>
				{isAdmin && (
					<Button
						variant="contained"
						startIcon={<Upload size={18} />}
						onClick={() => {
							resetForm();
							setUploadDialogOpen(true);
						}}
						sx={{
							backgroundColor: "#6366f1",
							"&:hover": { backgroundColor: "#4f46e5" },
						}}>
						Upload Document
					</Button>
				)}
			</Box>

			<Paper sx={{ overflow: "hidden" }}>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress />
					</Box>
				) : documents.length === 0 ? (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography color="text.secondary">No documents yet.</Typography>
						{isAdmin && (
							<Button
								variant="outlined"
								sx={{ mt: 2 }}
								onClick={() => setUploadDialogOpen(true)}>
								Upload your first document
							</Button>
						)}
					</Box>
				) : (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow sx={{ backgroundColor: "#f9fafb" }}>
									<TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>File</TableCell>
									<TableCell sx={{ fontWeight: 600 }} align="right">
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{documents.map((doc) => (
									<TableRow key={doc.id} hover>
										<TableCell>{doc.name}</TableCell>
										<TableCell sx={{ maxWidth: 300 }}>{doc.description || "—"}</TableCell>
										<TableCell>{doc.file_name}</TableCell>
										<TableCell align="right">
											<IconButton
												size="small"
												onClick={() => handleDownload(doc)}
												title="Download"
												sx={{ color: "#6366f1" }}>
												<Download size={18} />
											</IconButton>
											{isAdmin && (
												<>
													<IconButton
														size="small"
														onClick={() => openEditDialog(doc)}
														title="Edit">
														<Edit2 size={18} />
													</IconButton>
													<IconButton
														size="small"
														onClick={() => openDeleteDialog(doc)}
														title="Delete"
														sx={{ color: "#dc2626" }}>
														<Trash2 size={18} />
													</IconButton>
												</>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Paper>

			{/* Upload Dialog */}
			<Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Upload Document</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						label="Name"
						required
						value={form.name}
						onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
						sx={{ mt: 1, mb: 2 }}
					/>
					<TextField
						fullWidth
						label="Short description"
						multiline
						rows={2}
						value={form.description}
						onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
						sx={{ mb: 2 }}
					/>
					<Button variant="outlined" component="label" fullWidth>
						{form.file ? form.file.name : "Choose file"}
						<input
							type="file"
							hidden
							onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
						/>
					</Button>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => !uploading && setUploadDialogOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleUpload} disabled={uploading}>
						{uploading ? <CircularProgress size={20} /> : "Upload"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Edit Dialog */}
			<Dialog open={editDialogOpen} onClose={() => !uploading && setEditDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Edit Document</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						label="Name"
						required
						value={form.name}
						onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
						sx={{ mt: 1, mb: 2 }}
					/>
					<TextField
						fullWidth
						label="Short description"
						multiline
						rows={2}
						value={form.description}
						onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
						sx={{ mb: 2 }}
					/>
					<Button variant="outlined" component="label" fullWidth>
						{form.file ? form.file.name : "Replace file (optional)"}
						<input
							type="file"
							hidden
							onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
						/>
					</Button>
					{selectedDoc && !form.file && (
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
							Current: {selectedDoc.file_name}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => !uploading && setEditDialogOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleEdit} disabled={uploading}>
						{uploading ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete confirmation */}
			<Dialog open={deleteDialogOpen} onClose={() => !uploading && setDeleteDialogOpen(false)}>
				<DialogTitle>Delete document</DialogTitle>
				<DialogContent>
					{selectedDoc && (
						<Typography>
							Are you sure you want to delete &quot;{selectedDoc.name}&quot;? This cannot be undone.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => !uploading && setDeleteDialogOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button variant="contained" color="error" onClick={handleDelete} disabled={uploading}>
						{uploading ? <CircularProgress size={20} /> : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default Downloads;
